import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { IconButton, Tooltip } from '@material-ui/core';
import { useHotkeys } from 'react-hotkeys-hook';
import { Close } from '@material-ui/icons';
import cx from 'classnames';
import JSZip from 'jszip';

import { getAllContentVideofeedsThunk, getAllSharedVideoFeedsByFolderId, getAllVideoFeedsByFolderId } from '../../../store/videos/videoSlice';
import UploadFileIcon from '../../../assets/icons/upload-icon-file.svg';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { ContainedButton, GrayButton } from '../../modules/Buttons';
import { postAddJsonToVideo, postUploadJSON, postUploadMetaData, uploadJSON } from '../../../api';
import { findSearchValue, getAllContentSortOptionAndType, getSharedWithMePageSortOptionAndType, isSafari } from '../../../helpers';
import style from './UploadJsonModal.module.scss';
import { VIDEOFEED } from '../../../enums';
import Modal from '../../modules/Modal';
import { SortOptions, SortType } from '../../../enums/SORT';

type TModal = {
  open: boolean;
  switchModal: () => void;
  videoFeed: VIDEOFEED;
  onFinish: (nextFeedId?: number) => void;
  setTriggetForuseEffect?: (n: number) => void;
  fetchData: (forJson?: boolean) => void;
};

const safari = isSafari();
const MAX_SAFE_NAME = 180;
const toSafeJsonName = (name: string) => {
  const dot = name.lastIndexOf('.');
  const ext = dot > -1 ? name.slice(dot) : '';
  const base = dot > -1 ? name.slice(0, dot) : name;
  const limit = Math.max(1, MAX_SAFE_NAME - ext.length);
  return `${base.trim().slice(0, limit)}${ext}`;
};

const UploadJsonModal: React.FC<TModal> = ({ open, switchModal, videoFeed, onFinish, setTriggetForuseEffect, fetchData }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const zipRef = useRef<Blob>();
  const [minimized, setMinimized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const dispatch = useAppDispatch();
  const { currentUser } = useAppSelector((state) => state.user);
  const notification = useSnackbar();
  const uploadButtonRef = useRef<HTMLDivElement>(null);

  const onModalSwitch = (byPassLoading?: boolean) => {
    if (loading && !byPassLoading) {
      const result = confirm('Cancel uploading?');
      if (!result) {
        return;
      }
    }
    switchModal();
    setMinimized(false);
    setLoading(false);
    setFiles([]);
  };

  const minimizeModal = () => {
    setMinimized(!minimized);
  };

  const submitAsIs = async () => {
    try {
      setLoading(true);
      let lastCreatedFeedId: number | undefined;
      const isAllContent = findSearchValue('allContent') === 'true';
      for (const file of files) {
        const safeName = toSafeJsonName(file.name);
        const arrayBuf = await file.arrayBuffer();
        const lower = safeName.toLowerCase();
        const isXml = lower.endsWith('.xml') || lower.endsWith('.plist');
        const defaultType = isXml ? 'application/xml' : 'application/json';
        const rawFile = new File([arrayBuf], safeName, {
          type: file.type || defaultType,
        });

        const response = await postUploadJSON(rawFile, videoFeed.id, isXml, { allContent: isAllContent });
        const nextFeedId = Number(response?.data?.videofeedId || 0);
        if (nextFeedId > 0) {
          lastCreatedFeedId = nextFeedId;
        }
      }

      const folderId = findSearchValue('folderId');
      const shared = findSearchValue('shared');
      const allContent = findSearchValue('allContent');
      if (folderId) {
        if (shared) {
          if (allContent) {
            const sortRes = getAllContentSortOptionAndType() || 'asc/name';
            const [sortValue, sortOptionValue] = sortRes.split('/');
            await dispatch(
              getAllContentVideofeedsThunk({
                folderId,
                sort: (sortValue as SortType) || SortType.ASC,
                sortOption: ((sortOptionValue as SortOptions) || SortOptions.NAME) as SortOptions,
              }),
            );
          } else {
            const sortRes = getSharedWithMePageSortOptionAndType() || 'asc/name';
            const [sortValue, sortOptionValue] = sortRes.split('/');
            await dispatch(
              getAllSharedVideoFeedsByFolderId({
                folderId,
                sort: (sortValue as SortType) || SortType.ASC,
                sortOption: ((sortOptionValue as SortOptions) || SortOptions.NAME) as SortOptions,
              }),
            );
          }
        } else {
          await dispatch(getAllVideoFeedsByFolderId({ folderId }));
        }
        fetchData(true);
        onModalSwitch(true);
      }
      onFinish(lastCreatedFeedId);
    } catch (err) {
      console.log(err);
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const openUploader = () => {
    inputRef?.current?.click();
  };

  useEffect(() => {
    setFiles([]);
  }, []);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    let eventFiles = event.target.files;
    if (eventFiles && eventFiles.length > 0) {
      const validFiles = Array.from(eventFiles).filter((file) => {
        const name = file.name.toLowerCase();
        return name.endsWith('.json') || name.endsWith('.xml') || name.endsWith('.plist');
      });
      setFiles((prevFiles) => [...prevFiles, ...validFiles]);
    }
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files).filter((file) => {
      const name = file.name.toLowerCase();
      return name.endsWith('.json') || name.endsWith('.xml') || name.endsWith('.plist');
    });
    setFiles((prevFiles) => [...prevFiles, ...droppedFiles]);
  };

  useHotkeys('Enter', () => {
    if (!open || loading) return;

    uploadButtonRef?.current?.click();
  });

  return (
    <>
      <Modal className={minimized ? style.minimized : ''} open={open} onClose={open ? console.log : onModalSwitch} loading={loading}>
        <div className={style.modalBody}>
          <div className={style.modalTitle}>
            <span>Upload Content</span>
          </div>

          {!files.length ? (
            <div
              className={style.uploadZone}
              onClick={openUploader}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <div className={style.innerZone}>
                <img className={style.uploadIcon} src={UploadFileIcon} alt="upload file zone" />
                <div className={style.uploadText}>
                  Upload JSON, PLIST or Legacy XML files by drag and <br /> drop or click to upload
                </div>
              </div>
              {!files.length && <input type="file" multiple onChange={handleFileSelect} ref={inputRef} hidden accept=".json,.xml,.plist" />}
            </div>
          ) : (
            <div className={style.fileListZone}>
              <div className={style.fileList}>
                {files.map((file, i) => (
                  <div className={style.fileItem} key={`${file.name}_${i}`}>
                    <div className={style.fileLeftSide}>
                      <img className={style.fileIcon} src={UploadFileIcon} alt="upload file zone" />
                      <Tooltip title={file.name}>
                        <span>{toSafeJsonName(file.name)}</span>
                      </Tooltip>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {files.length > 0 && videoFeed.created_by.id === currentUser?.id && !safari && (
            <div
              className={style.uploadMore}
              onClick={openUploader}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <div className={style.uploadText}>Upload more</div>
            </div>
          )}

          <div className={style.footerButtons}>
            <ContainedButton disabled={!files.length || loading} className={style.uploadBtn} onClick={submitAsIs}>
              <div>{loading ? 'Uploading...' : 'Upload file'}</div>
            </ContainedButton>
            <br />
            <GrayButton className={style.outlinedBtn} onClick={onModalSwitch}>
              Cancel
            </GrayButton>
          </div>
        </div>
      </Modal>

      <div className={cx(style.mimizedModal, minimized ? '' : style.minimized)} onClick={minimizeModal}>
        {loading ? <div>Uploading Content...</div> : <div>Upload Content</div>}
        <div className={style.rightButtons}>
          <IconButton
            aria-label="close"
            color="primary"
            style={{
              color: 'white',
              padding: 0,
            }}
          >
            <Close />
          </IconButton>
        </div>
      </div>
    </>
  );
};

export default UploadJsonModal;
